<template>
  <v-container id="dashboard" fluid class="mb-4 d-flex pl-2 pb-0 pt-3">
    <v-row no-gutters class="d-flex">
      <v-col @mouseenter="showEditIcon = true" @mouseleave="showEditIcon = false">
        <div v-if="!editDashboardMode" class="inline-header-container">
          <h1
            :id="`name-dashboard-${currentDashboardState?.dashboard.id}`"
            class="itemis-analyze-headline"
            @dblclick="switchDashboardMode"
          >
            {{ dashboardName }}
          </h1>
        </div>
        <v-icon
          v-if="showEditIcon && !editDashboardMode"
          size="small"
          class="edit-icon pl-5 pb-4"
          @click="switchDashboardMode"
        >
          {{ CONSTANTS.ICONS_MDI.ICON_EDIT }}
        </v-icon>
        <v-text-field
          v-if="editDashboardMode"
          :id="`name-field-dashboard-${currentDashboardState?.dashboard.id}`"
          ref="nameTextField"
          v-model="dashboardName"
          variant="underlined"
          density="compact"
          class="pt-0"
          :hide-details="true"
          @keydown.enter="saveDashboardNameAndDescription"
          @keydown.esc="cancelEditDashboardMode"
        />
      </v-col>

      <v-spacer />

      <DashboardAddChartButton />
    </v-row>
  </v-container>
  <v-container fluid class="d-flex pl-2 pt-0 pb-0">
    <v-row no-gutters class="d-flex">
      <div
        v-if="!editDashboardMode"
        class="inline-header-container"
        @mouseenter="showEditIconDescription = true"
        @mouseleave="showEditIconDescription = false"
      >
        <h1
          :id="`description-dashboard-${currentDashboardState?.dashboard.id}`"
          class="itemis-analyze-text"
          @dblclick="switchDashboardMode"
        >
          {{ dashboardDescription }}
        </h1>
        <v-icon
          v-if="showEditIconDescription"
          size="small"
          class="edit-icon"
          @click="switchDashboardMode"
        >
          {{ CONSTANTS.ICONS_MDI.ICON_EDIT }}
        </v-icon>
      </div>
      <v-textarea
        v-else
        :id="`description-area-dashboard-${currentDashboardState?.dashboard.id}`"
        ref="descriptionTextField"
        v-model="dashboardDescription"
        density="compact"
        :hide-details="true"
        variant="underlined"
        auto-grow
        rows="1"
        row-height="15"
        @keydown.enter="saveDashboardNameAndDescription"
        @keydown.esc="cancelEditDashboardMode"
      />
    </v-row>
  </v-container>
</template>

<script lang="ts" setup>
  import labels from '@/translations/labels.json'
  import DashboardAddChartButton from '@/components/DashboardAddChartButton/DashboardAddChartButton.vue'
  import { onUnmounted, ref, watch } from 'vue'
  import { DashboardState, useDashboardStore } from '@/stores/dashboardStore'
  import { onMounted } from 'vue'
  import CONSTANTS from '@/constants/Config.constants'
  import { useRouter } from 'vue-router'
  export interface TextDashboardData {
    name: string
    description: string
  }

  interface Props {
    currentDashboardState: DashboardState | undefined
  }

  const props = defineProps<Props>()
  const router = useRouter()

  const showEditIcon = ref(false)
  const showEditIconDescription = ref(false)

  const dashboardStore = useDashboardStore()

  const dashboardState = ref<DashboardState | undefined>(props.currentDashboardState)
  const dashboardName = ref<string>(labels.DASHBOARD.DEFAULT_DASHBOARD_TITLE)
  const dashboardDescription = ref<string>(labels.DASHBOARD.DEFAULT_DASHBOARD_DESCRIPTION)
  const editDashboardMode = ref(false)

  onMounted(() => {
    updateNameAndDescription()
    document.addEventListener('keydown', handleGlobalKeyPress)
  })

  onUnmounted(() => {
    document.removeEventListener('keydown', handleGlobalKeyPress)
  })

  watch(
    () => props.currentDashboardState,
    () => {
      dashboardState.value = props.currentDashboardState
      updateNameAndDescription()
    },
  )

  watch(
    () => router.currentRoute.value.fullPath,
    () => {
      if (editDashboardMode.value) {
        cancelEditDashboardMode()
      }
    },
  )

  function saveDashboardNameAndDescription(): void {
    if (dashboardName.value.trim() === '') {
      dashboardName.value = labels.DASHBOARD.DEFAULT_DASHBOARD_TITLE
    }

    if (dashboardState.value) {
      dashboardState.value.dashboard.name = dashboardName.value
      dashboardState.value.dashboard.description = dashboardDescription.value
      dashboardStore.markDashboardModified()
    }

    switchDashboardMode()
  }

  function switchDashboardMode(): void {
    editDashboardMode.value = !editDashboardMode.value
    showEditIcon.value = false
    showEditIconDescription.value = false
  }

  function updateNameAndDescription() {
    dashboardName.value = dashboardState.value
      ? dashboardState.value?.dashboard.name
      : labels.DASHBOARD.DEFAULT_DASHBOARD_TITLE

    dashboardDescription.value = dashboardState.value
      ? dashboardState.value?.dashboard.description
      : labels.DASHBOARD.DEFAULT_DASHBOARD_DESCRIPTION
  }

  function cancelEditDashboardMode(): void {
    // Reset the text field value to the original dashboard title
    dashboardName.value = dashboardState.value
      ? dashboardState.value.dashboard.name
      : labels.DASHBOARD.DEFAULT_DASHBOARD_TITLE

    // Reset the text field value to the original dashboard description
    dashboardDescription.value = dashboardState.value
      ? dashboardState.value.dashboard.description
      : labels.DASHBOARD.DEFAULT_DASHBOARD_DESCRIPTION

    switchDashboardMode()
  }

  const handleGlobalKeyPress = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && editDashboardMode.value) {
      cancelEditDashboardMode()
    }
  }
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
  .inline-header-container {
    display: inline-flex;
    align-items: center;
  }

  .edit-icon {
    margin-left: 10px; /* Add space between the text and the icon */
  }
</style>
